import API, {APIRequestConfig} from "@irati/utils/services/API"

import {
  User,
  TenantOnboardingStepStates,
  UpdateTenantOnboardingInput,
  UserOnboarding,
  QueryGetOnboardingUrlArgs,
  TenantOnboarding,
  QueryGetTenantOnboardingListArgs
} from "@irati/types"
import authentication from "@irati/auth"
import client, {QueryOptions} from "@irati/utils/services/apollo"
import PBService from "@irati/utils/services"

import listFormat from "@irati/utils/services/graph/utils/listFormat"
import handleOnboardingDoesNotExist from "../helpers/handleOnboardingDoesNotExist"
import codes from "../errorCodes/legacy/Onboarding"
import endpoints from "../endpoints"
import OnboardingStep from "../enums/Step"
import onboardingErrorHandler from "./errors/legacy/onboardingErrorHandler"
import {getTenantOnboarding, getOnboardingUrl, getTenantOnboardingList, resendWelcomeEmail as resendWelcomeEmailQuery} from "./queries"
import {updateTenantOnboardingStep, updateTenantOnboarding, completeUserOnboardingStep, createBusinessOnboarding} from "./mutations"
import userOnboardingQuery, {userOnboardingKey} from "./queries/getUserOnboarding"
import removeUserOnboardingStep from "./mutations/removeUserOnboardingStep"

interface ResendWelcomeEmailAPIRequestConfig extends APIRequestConfig {
  createOnboardingOnFail?: boolean
}

class Onboarding extends PBService {
  // User Onboarding
  create = (user: User, options = {}) => {
    const data = {
      userId: user.uuid,
      tenantId: user.tenant,
      roles: user.roles,
      email: user.email,
      source: user.source
    }
    return API.post(`${endpoints.createOnboarding()}`, data, options)
  }

  get = (userId: string, options: QueryOptions = {}): Promise<UserOnboarding> => {
    const variables = {
      userId
    }
    // remove when we use update mutation properly
    return client.query({...options, query: userOnboardingQuery, variables}).then((res) => res.data[userOnboardingKey])
  }

  getOnboardingUrl = (userId: string) => {
    const variables: QueryGetOnboardingUrlArgs = {
      userId
    }
    return client.query({query: getOnboardingUrl, variables, fetchPolicy: "network-only"}).then((res) => res.data.getOnboardingUrl)
  }

  resendWelcomeEmail = (user: User, options: ResendWelcomeEmailAPIRequestConfig = {}) => {
    const variables = {
      userId: user.uuid
    }
    API.sentryIgnoreError(options, [codes.InitialOnboardingCompleted, codes.UserDisabled])
    return client
      .query({query: resendWelcomeEmailQuery, variables})
      .then((res) => res.data.resendWelcomeEmailQuery)
      .catch((err) => handleOnboardingDoesNotExist(err, user))
  }

  // Business Onboarding
  createBusinessOnboarding = (businessId: string): Promise<TenantOnboarding> => {
    const variables = {
      businessId
    }
    return client.mutate({mutation: createBusinessOnboarding, variables}).then((res) => res.data.createBusinessOnboarding)
  }

  getTenantOnboarding = (tenantId = authentication.userTenant().uuid, options: QueryOptions = {}): Promise<TenantOnboarding> => {
    const variables = {
      tenantId
    }
    return client.query({...options, query: getTenantOnboarding, variables}).then((res) => res.data.getTenantOnboarding)
  }

  getTenantOnboardingList = (variables: QueryGetTenantOnboardingListArgs = {}, options: QueryOptions = {}) => {
    return client.query({...options, query: getTenantOnboardingList, variables}).then((res) => {
      return listFormat(res.data.getTenantOnboardingList)
    })
  }

  updateTenantOnboardingStep = (stepId: string, state: TenantOnboardingStepStates, selection: number) => {
    const variables = {
      tenantId: authentication.userTenant().uuid,
      stepId,
      variables: {
        state,
        selection
      }
    }
    return client.mutate({mutation: updateTenantOnboardingStep, variables}).then((res) => res.data.updateTenantOnboardingStep)
  }

  updateTenantOnboarding = (params: UpdateTenantOnboardingInput, tenantId?: string) => {
    const variables = {
      tenantId: tenantId || authentication.userTenant().uuid,
      variables: params
    }
    return client.mutate({mutation: updateTenantOnboarding, variables}).then((res) => res.data.updateTenantOnboarding)
  }

  completeStep = (user: User, step: OnboardingStep) => {
    const variables = {
      userId: user.uuid,
      stepId: step
    }
    return client.mutate({mutation: completeUserOnboardingStep, variables}).then((res) => res.data.completeUserOnboardingStep)
  }

  removeStep = (user: User, step: OnboardingStep) => {
    const variables = {
      userId: user.uuid,
      stepId: step
    }
    return client.mutate({mutation: removeUserOnboardingStep, variables}).then((res) => res.data.removeUserOnboardingStep)
  }
}

const onboarding = new Onboarding({errorHandlers: [onboardingErrorHandler]})

export const resendWelcomeEmail = onboarding.resendWelcomeEmail
export const completeOnboardingStep = onboarding.completeStep
export const getOnboarding = onboarding.get
export const createOnboarding = onboarding.create
export {OnboardingStep}

export default onboarding
